import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clockTime,
  cycleLabel,
  formatDuration,
  formatLeft,
  jobTypeLabel,
  parseIso,
  shortCycle,
} from './pipelineFormat';

const name = (j) => j.student_name || (j.student_id ? `Student #${j.student_id}` : 'Unknown student');

/** Estimated start times assuming FIFO: position 1 starts when the running job finishes. */
function queueStarts(running, queued, now) {
  let cursor = Math.min(...running.map((j) => Number(j.remaining_seconds) || 0), running.length ? Infinity : 0);
  if (!Number.isFinite(cursor)) cursor = 0;
  return queued.map((q) => {
    const startIn = cursor;
    cursor += Number(q.estimated_seconds) || 0;
    return new Date(now + startIn * 1000);
  });
}

function cycleChips(job) {
  const cycles = Array.isArray(job.cycles) ? job.cycles : [];
  return cycles
    .filter((c) => c.seconds || c.anchor)
    .map((c) => `${shortCycle(c.anchor) || cycleLabel(c.anchor)}${c.seconds ? ` · ${formatDuration(c.seconds)}` : ''}`);
}

function finishedLabel(job) {
  const status = String(job.status || '').toLowerCase();
  if (status === 'completed') return { text: 'Done', tone: 'text-emerald-700 dark:text-emerald-300' };
  if (status === 'cancelled' || /stopped by/i.test(String(job.error || ''))) {
    return { text: 'Stopped', tone: 'text-slate-600 dark:text-slate-300' };
  }
  return { text: 'Failed', tone: 'text-red-600 dark:text-red-300' };
}

const TABS = [
  { key: 'queued', label: 'Queued' },
  { key: 'paused', label: 'Paused' },
  { key: 'finished', label: 'Finished' },
];

function JobDetail({ job, kind, startAt, canControl, busy, onAction }) {
  const done = kind === 'finished' ? parseIso(job.completed_at) : null;
  const outcome = kind === 'finished' ? finishedLabel(job) : null;
  const chips = cycleChips(job);
  const pages = Number(job.total_pages) || 0;
  return (
    <div className="border-b border-gray-100 bg-slate-50 px-4 py-3.5 dark:border-slate-700 dark:bg-slate-900/40">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-gray-900 dark:text-white">{name(job)}</p>
          <p className="mt-0.5 text-[12px] text-gray-500 dark:text-slate-400">
            {jobTypeLabel(job)}
            {pages ? ` · ${pages} pages` : ''}
            {job.started_by_name ? ` · started by ${job.started_by_name}` : ''}
            {job.paused_by_name ? ` · paused by ${job.paused_by_name}` : ''}
          </p>
        </div>
        <div className="text-right">
          {kind === 'queued' ? (
            <>
              <p className="text-[13px] font-semibold text-gray-900 dark:text-white">{startAt ? `Starts ≈ ${clockTime(startAt)}` : 'Waiting for the worker'}</p>
              <p className="text-[11.5px] text-gray-500 dark:text-slate-400">
                {formatLeft(job.estimated_seconds) ? `About ${formatLeft(job.estimated_seconds)} once it starts` : 'One worker — starts when the running job finishes'}
              </p>
            </>
          ) : null}
          {kind === 'paused' ? (
            <>
              <p className="text-[13px] font-semibold text-gray-900 dark:text-white">Paused</p>
              <p className="text-[11.5px] text-gray-500 dark:text-slate-400">Resume within 30 days</p>
            </>
          ) : null}
          {kind === 'finished' ? (
            <>
              <p className={`text-[13px] font-semibold ${outcome.tone}`}>{outcome.text}</p>
              <p className="text-[11.5px] text-gray-500 dark:text-slate-400">
                {job.duration_seconds != null ? formatDuration(job.duration_seconds) : '—'}
                {done ? ` · ${clockTime(done)}` : ''}
              </p>
            </>
          ) : null}
        </div>
      </div>
      {job.message ? <p className="mt-2 text-[12.5px] text-gray-700 dark:text-slate-200">{job.message}</p> : null}
      {kind === 'finished' && outcome.text !== 'Done' && job.error ? (
        <p className="mt-2 text-[12px] text-red-600 dark:text-red-300">{job.error}</p>
      ) : null}
      {chips.length ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {chips.map((c) => (
            <span key={c} className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10.5px] font-medium text-gray-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">{c}</span>
          ))}
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {kind === 'queued' && canControl && job.can_pause ? (
          <button type="button" disabled={busy} onClick={() => onAction('pause', job)} className="min-h-[32px] rounded-lg border border-amber-300 px-2.5 text-[12px] font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30">Pause</button>
        ) : null}
        {kind === 'paused' && canControl && job.can_start ? (
          <button type="button" disabled={busy} onClick={() => onAction('start', job)} className="min-h-[32px] rounded-lg bg-emerald-600 px-2.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Start</button>
        ) : null}
        {(kind === 'queued' || kind === 'paused') && canControl && job.can_stop ? (
          <button type="button" disabled={busy} onClick={() => onAction('stop', job)} className="min-h-[32px] rounded-lg border border-red-200 px-2.5 text-[12px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300">Stop</button>
        ) : null}
        {kind === 'finished' && canControl && job.can_restart ? (
          <button type="button" disabled={busy} onClick={() => onAction('restart', job)} className="min-h-[32px] rounded-lg border border-gray-300 px-2.5 text-[12px] font-semibold text-gray-700 hover:bg-white disabled:opacity-50 dark:border-slate-600 dark:text-slate-200">Restart</button>
        ) : null}
        {job.student_id ? (
          <Link to={`/students/${job.student_id}/information`} className="inline-flex min-h-[32px] items-center rounded-lg bg-indigo-600 px-2.5 text-[12px] font-semibold text-white hover:bg-indigo-700">Open report</Link>
        ) : null}
      </div>
    </div>
  );
}

export default function StatusPane({ data, now, canControl, busyId, onAction }) {
  const { running, queued, paused, recent } = data;
  const [tab, setTab] = useState('queued');
  const [selectedId, setSelectedId] = useState(null);
  const starts = queueStarts(running, queued, now);
  const lists = { queued, paused, finished: recent };
  const jobs = lists[tab] || [];

  const counts = { queued: queued.length, paused: paused.length, finished: recent.length };
  const hints = {
    queued: '',
    paused: 'resume within 30 days',
    finished: 'last 24 hours',
  };
  const empty = {
    queued: 'Nothing waiting.',
    paused: 'No paused jobs.',
    finished: 'No jobs finished in the last 24 hours.',
  };

  const pickTab = (key) => {
    setTab(key);
    setSelectedId(null);
  };

  return (
    <section className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800/80" aria-label="Queued, paused, and finished jobs">
      <div role="tablist" aria-label="Job lists" className="grid shrink-0 grid-cols-3 border-b border-gray-100 dark:border-slate-700">
        {TABS.map((t) => {
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => pickTab(t.key)}
              className={`min-h-[64px] px-2 py-2.5 text-center transition-colors ${
                on
                  ? 'bg-indigo-50 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-100'
                  : 'text-gray-600 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-700/50'
              } ${t.key !== 'finished' ? 'border-r border-gray-100 dark:border-slate-700' : ''}`}
            >
              <span className="block text-[12px] font-semibold">{t.label}</span>
              <span className="mt-0.5 block text-lg font-bold leading-none tabular-nums">{counts[t.key]}</span>
            </button>
          );
        })}
      </div>

      <header className="flex shrink-0 items-baseline gap-2 px-4 py-2.5">
        <h2 className="text-[13px] font-semibold text-gray-900 dark:text-white">{TABS.find((t) => t.key === tab).label}</h2>
        {hints[tab] ? (
          <span className="ml-auto text-[11px] text-gray-500 dark:text-slate-400">{hints[tab]}</span>
        ) : null}
      </header>

      {jobs.length ? (
        <ul className="max-h-[20.5rem] divide-y divide-gray-100 overflow-y-auto [scrollbar-width:thin] dark:divide-slate-700">
          {jobs.map((job, i) => {
            const on = job.job_id === selectedId;
            const outcome = tab === 'finished' ? finishedLabel(job) : null;
            const meta = tab === 'queued'
              ? (starts[i] ? `≈ ${clockTime(starts[i])}` : 'Waiting')
              : tab === 'paused'
                ? 'Paused'
                : (job.duration_seconds != null ? formatDuration(job.duration_seconds) : '—');
            return (
              <li key={job.job_id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(on ? null : job.job_id)}
                  aria-pressed={on}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                    on ? 'bg-indigo-50/80 dark:bg-indigo-500/10' : 'hover:bg-gray-50 dark:hover:bg-slate-700/40'
                  }`}
                >
                  <span className={`mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                    tab === 'paused'
                      ? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                      : tab === 'finished'
                        ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                  }`}
                  >
                    {tab === 'queued' ? i + 1 : tab === 'paused' ? 'Ⅱ' : '✓'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-gray-900 dark:text-white">{name(job)}</span>
                    <span className="block truncate text-[11.5px] text-gray-500 dark:text-slate-400">
                      {jobTypeLabel(job)}{Number(job.total_pages) ? ` · ${job.total_pages} pages` : ''}
                    </span>
                  </span>
                  <span className="text-right">
                    <span className={`block text-[12.5px] font-semibold ${outcome ? outcome.tone : 'text-gray-900 dark:text-white'}`}>{outcome ? outcome.text : meta}</span>
                    {tab === 'finished' ? <span className="block text-[11px] text-gray-500 dark:text-slate-400">{meta}</span> : null}
                    {tab === 'queued' && formatLeft(job.estimated_seconds) ? (
                      <span className="block text-[11px] text-gray-500 dark:text-slate-400">≈ {formatLeft(job.estimated_seconds)}</span>
                    ) : null}
                  </span>
                </button>
                {on ? (
                  <JobDetail
                    job={job}
                    kind={tab}
                    startAt={tab === 'queued' ? starts[i] : null}
                    canControl={canControl}
                    busy={busyId === job.job_id}
                    onAction={onAction}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="px-4 py-4 text-[12.5px] text-gray-500 dark:text-slate-400">{empty[tab]}</p>
      )}
    </section>
  );
}
